import { useEffect, useState } from "react";
import DataPanel from "../DataPanel";
import OrderDetails from "./OrderDetails";
import { OrderNumber, parseOrderNumber } from "./OrderNumber";
import { OrderStatus } from "../../api/OrderApi";
import { ClassNameMap } from "@material-ui/core/styles/withStyles";
import OrderedMaterialsStatisticsButton from "./OrderedMaterialsStatisticsButton";
import useCustomerApi from "../../api/CustomerApi.hook";

interface Props {
  customerId?: number|null;
}

interface Customer {
  id: number;
  name: string;
}

const columns = [
  {
    name: 'number',
    label: 'Numer',
    options: {
      filter: false,
      sort: true
    }
  },
  {
    name: 'creationDate',
    label: 'Data zamówienia',
    options: {
      filter: false,
      sort: true
    }
  },
  {
    name: 'completionDate',
    label: 'Data realizacji',
    options: {
      filter: false,
      sort: true
    }
  },
  {
    name: 'status',
    label: 'Status',
    options: {
      filter: true,
      sort: true
    }
  }
];

const getCustomerId = (customerId: number|null|undefined, orderData: any) => {
  console.debug(`getCustomerId: orderData=${JSON.stringify(orderData)}`);
  if (customerId === undefined || customerId === null) {
    return orderData?.customer.id;
  } else {
    return customerId;
  }
};

const getColumns = (customerId: number|null|undefined) => {
  if (customerId === undefined || customerId === null) {
    return [...columns, {
      name: 'customerName',
      label: 'Klient',
      options: {
        filter: false,
        sort: true
      }
    }]
  } else {
    return columns;
  }
};

const compareOrderNumber = (orderNumber: OrderNumber, anotherOrderNumber: OrderNumber, order: string) => {
  return orderNumber === null ? (order === 'asc' ? -1 : 1)
    : anotherOrderNumber === null ? (order === 'asc' ? 1 : -1)
    : orderNumber.compare(anotherOrderNumber);
};

const sortData = (data: any[], columnIndex: number, order: string) => {
  data.sort((row, anotherRow) => {
    const cellValue = row.data[columnIndex];
    const anotherCellValue = anotherRow.data[columnIndex];
    const comparisonResult
      = cellValue === undefined ? -1
      : anotherCellValue === undefined ? 1
      : columnIndex === 0 ? compareOrderNumber(cellValue.props['data-orderNumber'], anotherCellValue.props['data-orderNumber'], order)
      : cellValue.localeCompare(anotherCellValue)
    return order === 'asc' ? comparisonResult : -comparisonResult;
  });
  return data;
};

export default (props: Props) => {
  const customerId = props.customerId;
  const customerApi = useCustomerApi();
  const [customers, setCustomers] = useState<Customer[]>();

  const isCustomerEmpty = customerId === undefined || customerId === null;

  const findCustomer = (customerId: number) => customers?.find(customer => customer.id === customerId);

  const getDisplayedOrderStatus = (orderStatus : string) => {
    switch (orderStatus) {
      case OrderStatus.Created : return 'utworzone'
      case OrderStatus.InProgress : return 'w trakcie'
      case OrderStatus.Complete : return 'zakończone'
      default :
    }
    return '<?>';
  };

  const convertToOrder = (data: any) => {
    const orderNumber = parseOrderNumber(data.number);
    const style = orderNumber === null ? {color: 'red'} : undefined;
    const status = getDisplayedOrderStatus(data.status);
    return {
      ...data,
      number: (<div style={style} data-orderNumber={orderNumber}>{data.number}</div>),
      customerName: findCustomer(data.customer.id)?.name,
      status
    }
  };

  const renderCustomButtons = (
    classes: ClassNameMap,
    getSelectedIds: () => number[],
    fetchData: () => void,
    reportAlerts: (messages: string[]) => void
  ) => <OrderedMaterialsStatisticsButton buttonClasses={classes} />;

  useEffect(() => {
    if (isCustomerEmpty) {
      if (customerApi.ready) {
        customerApi
          .fetchCustomers()
          .then(setCustomers);
      }
    }
  }, [customerId, customerApi.ready]);

  return (
    <DataPanel
      title='Zamówienia'
      resource={ isCustomerEmpty ? `orders` : `customers/${customerId}/orders` }
      columns={ getColumns(customerId) }
      convert={ convertToOrder }
      createEnabled={ !isCustomerEmpty }
      deleteEnabled={ !isCustomerEmpty }
      deleteRequiresConfirmation
      tableSearchEnabled
      renderDetails={(detailsProps, orderData) =>
        <OrderDetails { ...{
          ...detailsProps,
          customerId: getCustomerId(customerId, orderData)
        }}/>}
      renderCustomButtons={ renderCustomButtons }
      sortData={ sortData }
      defaultSortOrder={{ name: 'number', direction: 'desc' }}
    />
  );
};